/**
 * Video-first Help (ADR-0025): one entry per Video script in `docs/help-videos/**`.
 * The scripts' frontmatter is the registry; `npm run help:registry` generates
 * `help-registry.generated.ts` from it, and `verify:help-content` keeps the two in sync.
 */

/** Help sections, in the order the Help page shows them (side-nav order, Flows first). */
export const HELP_SECTIONS = [
  { id: 'flows', label: 'Flows' },
  { id: 'clients', label: 'Clients' },
  { id: 'posture-analysis', label: 'Posture Analysis' },
  { id: 'sop', label: 'SoP' },
  { id: 'remote-assessment', label: 'Remote Assessment' },
  { id: 'drills', label: 'Drills' },
  { id: 'ask-harry', label: 'Ask Harry' },
  { id: 'progression-model', label: 'Progression Model' },
  { id: 'settings', label: 'Settings' },
  { id: 'admin', label: 'Admin' },
] as const;

export type HelpSectionId = (typeof HELP_SECTIONS)[number]['id'];

export const HELP_SECTION_IDS: readonly string[] = HELP_SECTIONS.map((section) => section.id);

/**
 * `subsection`: a task video (1 to 2 minutes); `flow`: a longer journey (Flows section);
 * `feature`: a 15 to 45 second clip of one function, listed under "On this screen".
 */
export type HelpEntryKind = 'subsection' | 'flow' | 'feature';

/** Feature clips are listed apart from the task videos and Flows. */
export function isFeatureClip(entry: Pick<HelpEntry, 'kind'>): boolean {
  return entry.kind === 'feature';
}

/** `app`: signed-in users; `admin`: admins only; `public`: also used on the public self-assessment. */
export type HelpAudience = 'app' | 'admin' | 'public';

export type HelpGate = 'experimentalFeatures' | null;

export interface HelpEntry {
  id: string;
  kind: HelpEntryKind;
  section: HelpSectionId;
  title: string;
  audience: HelpAudience;
  gatedBy: HelpGate;
  /** One sentence: what the viewer can do after watching. */
  purpose: string;
  /** Route patterns (`/clients/:id`) where the in-context Help shows this entry. */
  routes: string[];
  /** Further route patterns that offer this entry in context. */
  alsoShownIn: string[];
  /** Interim steps, shown under "Video coming soon" until a video is published. */
  steps: string[];
  /** Flows only: at most one paragraph. */
  flowText: string | null;
  /** At most one paragraph on a complex idea. */
  keyConcept: string | null;
  /** Other entries this one points to (Flows). */
  seeAlso: string[];
  /** Bucket-relative Storage path of the MP4, or null while no video is published. */
  video: string | null;
  /** Bucket-relative Storage path of the WebVTT captions. */
  captions: string | null;
  /** Bucket-relative Storage path of the poster frame (JPEG) shown on the tile, or null. */
  poster: string | null;
  /** Length of the published video in whole seconds, or null. */
  durationSec: number | null;
  recordedAt: string | null;
}
